import { useEffect, useRef, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import AppLoading from "../app-loading/app-loading";
import RawMaterialService from "../../services/RawMaterials/RawMaterialService";
import { RMOutwardChallan } from "./RMOutwardChallan-Datatable";
import { POSModel } from "../../models/orders/order-list-model";
import styles from "./RMOutwardChallanPage.module.css";

const RMOutwardChallanPage = () => {
  const toast = useRef<Toast>(null);

  const [loading, setLoading] = useState(false);
  const [posList, setPosList] = useState<POSModel[]>([]);
  const [selectedPosId, setSelectedPosId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [logData, setLogData] = useState<any[]>([]);
  const [challanCount, setChallanCount] = useState(0);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    RawMaterialService.GetManufacrturingunitlist().then((res: any) => {
      setPosList(res || []);
      if (res?.length === 1) setSelectedPosId(res[0].posID);
    });
  }, []);

  const formatDateOnly = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

  const handleSearch = async () => {
    if (!selectedPosId || !startDate || !endDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "POS and date range are required",
      });
      return;
    }

    setLoading(true);

    try {
      const result = await RawMaterialService.GetGetRawMaterialOutwardChallan({
        posId: selectedPosId,
        startDate: formatDateOnly(startDate),
        endDate: formatDateOnly(endDate),
      });

      setLogData(result?.movemementresult || []);
      setChallanCount(result?.challanCount || 0);
    } catch {
      setLogData([]);
      setChallanCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedPosId(null);
    setStartDate(null);
    setEndDate(null);
    setLogData([]);
    setChallanCount(0);
    setResetKey((k) => k + 1);
  };

  return (
    <div className={styles.page}>
      <Toast ref={toast} position="top-right" />

      {/* Unified Header + Filter Card */}
      <div className={styles.mainCard}>
        {/* Header */}
        {/* Header */}
        <div className={styles.headerStrip}>
          <div className={styles.headerLeft}>
            <div className={styles.icon}>
              <i className="pi pi-refresh" />
            </div>
            <div>
              <h2>Raw Material Outward Challan</h2>
              <p>Track, manage, and view raw material outward movements</p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className={styles.divider} />

        {/* Filter Section */}
        <div className={styles.filterSection}>
          <div className={styles.filterTitle}>
            <i className="pi pi-filter" />
            <span>Filter Movements</span>
          </div>

          <div className={styles.filterRow}>
            <Dropdown
              value={selectedPosId}
              options={posList}
              optionLabel="posCode"
              optionValue="posID"
              placeholder="Select POS"
              onChange={(e) => setSelectedPosId(e.value)}
              showClear
              filter
            />

            <Calendar
              key={`start-${resetKey}`}
              value={startDate}
              onChange={(e) => setStartDate(e.value as Date)}
              placeholder="Start Date"
              dateFormat="dd/mm/yy"
              showIcon
              readOnlyInput
              maxDate={endDate || undefined}
            />

            <Calendar
              key={`end-${resetKey}`}
              value={endDate}
              onChange={(e) => setEndDate(e.value as Date)}
              placeholder="End Date"
              dateFormat="dd/mm/yy"
              showIcon
              readOnlyInput
              minDate={startDate || undefined}
            />

            <Button
              label="Filter"
              icon="pi pi-search"
              onClick={handleSearch}
              className={styles.filterBtn}
            />

            <Button
              label="Reset"
              icon="pi pi-refresh"
              onClick={handleReset}
              className={styles.resetBtn}
              outlined
            />
          </div>
        </div>
      </div>


      {/* Table */}
      <RMOutwardChallan
        data={logData}
        challanCount={challanCount}
        refreshData={handleSearch}
      />

      {/* Loading */}
      {loading && (
        <div className={styles.loadingOverlay}>
          <AppLoading />
          <span>Loading data...</span>
        </div>
      )}
    </div>
  );
};

export default RMOutwardChallanPage;
